import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import RetroGridBg from "../RetroGridBg";
import { VisibilityOffRounded, VisibilityRounded } from "@mui/icons-material";
import LawyerGroupImg from "../../assets/lawyer-group.webp";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { backend_api } from "../../static/server";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import companyLogo from "../../assets/nav-lexmom.webp";
import wave_lins from "../../assets/wave-line.png";

const ResetPassForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [showPassword, setShowPassword] = useState(false);
  const [expiredLink, setExpiredLink] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const token = searchParams.get("token");

  // Check Valid Link from email or not. If expired then show expired message
  useEffect(() => {
    const verifyResetLink = async () => {
      try {
        if (!token) {
          navigate("/");
          return;
        }

        const response = await axios.get(
          `${backend_api}user/forget-password/?verify_code=${token}`,
        );
        if (response?.data?.expire) {
          toast.error("Link has expired", { toastId: "expire-toast" });
          setExpiredLink(true);
        }
        if (response?.data?.verify) {
          toast.success("Link is verified", { toastId: "verify-toast" });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    verifyResetLink();
  }, [token, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const body = new URLSearchParams();
      body.append("token_code", token);
      body.append("password", data?.password);

      const response = await axios.post(
        `${backend_api}user/forget-password/`,
        body,
      );

      if (response.status === 200) {
        toast.success("Password reset successfully");
        setIsEmailSent(true);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } catch (error) {
      console.error("Password reset failed", error);
      toast.error("Password reset failed. Please try again.");
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setValue(name, value);
    if (name === "password") {
      trigger("confirmPassword");
    }
  };

  const getFormActionText = () => {
    return expiredLink ? "Go to Login" : "Reset Password";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column-reverse", md: "row" },
        p: { xs: 0, md: 0 },
      }}
    >
      {/* Left Side - Form */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, x: -100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
        sx={{
          width: { xs: 1, md: 1 / 2 },
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: { xs: 2, md: 3 },
          overflow: "hidden",
          position: "relative",
          borderRadius: { xs: "1rem", md: "2rem" },
          minHeight: { xs: "100vh", md: "auto" },
        }}
      >
        <Box
          sx={{
            width: 1,
            maxWidth: "27.5rem",
            p: { xs: 2, md: 3 },
            zIndex: 5,
            borderRadius: 2,
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            border: "1px solid",
            borderColor: "divider",
            transition: "all 0.3ms",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: { xs: 2, md: 4 },
            }}
          >
            <Box
              sx={{
                height: { xs: "2.5rem", md: "3rem" },
                mb: 1,
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            >
              <img
                src={companyLogo}
                alt="Company Logo"
                style={{
                  height: "100%",
                  width: "auto",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.onerror = null;
                  e.target.src = require("../../assets/nav-lexmom.webp");
                }}
              />
            </Box>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              {expiredLink ? "Link Expired" : "Reset Password"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {expiredLink
                ? "Your password reset link has expired"
                : "Enter your new password to reset your account"}
            </Typography>
          </Box>

          {expiredLink ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "1rem",
                textAlign: "center",
                py: 3,
              }}
            >
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Your password reset link has expired. Please request a new one.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={() => navigate("/login")}
                sx={{ minWidth: "150px" }}
              >
                Go to Login
              </Button>
            </Box>
          ) : isEmailSent ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography
                variant="body1"
                color="primary.main"
                sx={{ pb: 2, fontSize: "1.2rem", fontWeight: "500" }}
              >
                Password Reset Successful!
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: "0.9rem", mb: 3 }}
              >
                Your password has been reset successfully. You will be
                redirected to the login page shortly.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/login")}
              >
                Go to Login Now
              </Button>
            </Box>
          ) : (
            <Box
              component="form"
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: "1rem", md: "0.9rem" },
                width: 1,
                maxWidth: "sm",
                "& > *": { mb: { xs: 1, md: 2 } },
              }}
              onSubmit={handleSubmit(onSubmit)}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  name="password"
                  onChange={handlePasswordChange}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 6,
                      message: "Password must be at least 6 characters",
                    },
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" size="small">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                          color="primary"
                        >
                          {showPassword ? (
                            <VisibilityOffRounded sx={{ fontSize: "1rem" }} />
                          ) : (
                            <VisibilityRounded sx={{ fontSize: "1rem" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  size="small"
                  fullWidth
                  label="Confirm Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  name="confirmPassword"
                  onChange={handlePasswordChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (value) => {
                      if (value !== password) {
                        return "Passwords do not match";
                      }
                      return true;
                    },
                  })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" size="small">
                        <IconButton
                          onClick={handleClickShowPassword}
                          edge="end"
                          color="primary"
                        >
                          {showPassword ? (
                            <VisibilityOffRounded sx={{ fontSize: "1rem" }} />
                          ) : (
                            <VisibilityRounded sx={{ fontSize: "1rem" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <LoadingButton
                type="submit"
                loading={loading}
                loadingPosition="start"
                variant="contained"
                fullWidth
                disabled={
                  !password || !confirmPassword || password !== confirmPassword
                }
                size="medium"
                sx={{ mt: 2 }}
              >
                {getFormActionText()}
              </LoadingButton>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            zIndex: 1,
            top: 0,
            left: 0,
          }}
        >
          <RetroGridBg />
        </Box>
      </Box>

      {/* Right Side - Image */}
      {!isSmallScreen && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          sx={{
            width: { md: 1 / 2 },
            position: "relative",
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            p: 3,
          }}
        >
          <Box
            sx={{
              width: 1,
              height: 1,
              position: "absolute",
              background: "linear-gradient(90deg, #472ed100 0%, #472ed1 100%)",
              zIndex: 0,
            }}
          >
            <img
              src={wave_lins}
              alt="Wave"
              style={{
                height: "100%",
                width: "100%",
                objectFit: "cover",
                mixBlendMode: "overlay",
              }}
              onError={(e) => {
                e.onerror = null;
                e.target.src = require("../../assets/wave-line.png");
              }}
            />
          </Box>

          <Box
            sx={{
              width: 1,
              height: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
              color: "white",
              textAlign: "center",
              p: 3,
            }}
          >
            <Box sx={{ height: "20rem", mb: 4 }}>
              <img
                src={LawyerGroupImg}
                alt="Lawyer Group"
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "contain",
                  filter: "brightness(1.1)",
                }}
                onError={(e) => {
                  e.onerror = null;
                  e.target.src = require("../../assets/no-preview.webp");
                }}
              />
            </Box>
            <Typography
              variant="body1"
              sx={{ mb: 1, fontSize: "1.4rem", fontWeight: 600 }}
            >
              {expiredLink ? "Secure Your Account" : "Reset Your Password"}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
              {expiredLink
                ? "Security is our top priority. Request a new reset link to continue."
                : "Set a new strong password to secure your legal practice account."}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ResetPassForm;
